// ChatSystem.js (FirestoreService-integrated)
// Replaces direct firestore calls with centralized firestoreService helpers

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../firebase/authContext';

// NOTE: these functions come from the firestoreService.js we added earlier
import {
  makeChatId,
  ensureChatDoc,
  sendMessageToChat,
  listenToMessages,
  markChatMessagesRead,
  setTyping,
  clearTyping,
  listenTyping,
  setUserOnline,
  setUserOffline,
  listenUserStatus,
  listenUsers,
  listenToUserChats
} from '../../firebase/firestoreService';

import {
  FiSend, FiUser, FiMessageCircle, FiMoreVertical, FiSearch, FiSmile,
  FiPaperclip, FiImage, FiClock
} from 'react-icons/fi';

import { BsThreeDots, BsCheck2All, BsCheck2 } from 'react-icons/bs';

const ChatSystem = () => {
  const { currentUser } = useAuth();

  // UI / data state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTypingLocal, setIsTypingLocal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [userChats, setUserChats] = useState({}); // { userId: { lastMessage, unreadCount } }
  const [firebaseAvailable, setFirebaseAvailable] = useState(false);

  // refs
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const listenersRef = useRef([]); // store unsubscribe functions (for chat-level listeners)
  const perUserMessageUnsubs = useRef({}); // { userId: unsub }

  // check firebase availability
  useEffect(() => {
    // db presence is checked inside firestoreService; keep flag for demo fallback UI
    setFirebaseAvailable(true);
  }, []);

  // scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => scrollToBottom(), [messages]);

  //
  // Presence: set user online/offline
  //
  useEffect(() => {
    if (!currentUser) return;
    // set online
    setUserOnline(currentUser.uid, currentUser.displayName || currentUser.email || '');

    const handleBeforeUnload = async () => {
      await setUserOffline(currentUser.uid, currentUser.displayName || currentUser.email || '');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // best-effort set offline on unmount
      setUserOffline(currentUser.uid, currentUser.displayName || currentUser.email || '').catch(err => {
        // ignore
      });
    };
  }, [currentUser]);

  //
  // Listen to global userStatus to compute onlineUsers set
  //
  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenUserStatus((statuses) => {
      const setOnline = new Set();
      statuses.forEach(s => {
        if (s.status === 'online' && s.uid && s.uid !== currentUser.uid) {
          setOnline.add(s.uid);
        }
      });
      setOnlineUsers(setOnline);
    }, (err) => {
      console.error("listenUserStatus error:", err);
    });

    listenersRef.current.push(unsub);
    return () => { try { unsub(); } catch(e){} };
  }, [currentUser]);

  //
  // Listen users collection (for users list). Demo fallback if no users.
  //
  useEffect(() => {
    if (!currentUser) return;

    setIsLoading(true);
    const unsub = listenUsers((usersList) => {
      const filtered = usersList.filter(u => u.id !== currentUser.uid);
      // if collection empty, keep demo users (like earlier behavior)
      if (filtered.length === 0) {
        const demoUsers = [
          { id: 'demo1', username: 'DanceMaster', email: 'dancemaster@example.com', bio: 'Professional Dance Instructor', profileImage: null },
          { id: 'demo2', username: 'HipHopQueen', email: 'hiphopqueen@example.com', bio: 'Hip Hop Dancer & Choreographer', profileImage: null },
          { id: 'demo3', username: 'BollywoodStar', email: 'bollywoodstar@example.com', bio: 'Bollywood Dance Specialist', profileImage: null }
        ];
        setUsers(demoUsers.filter(u => u.id !== currentUser.uid));
      } else {
        setUsers(filtered);
      }
      setIsLoading(false);
    }, (err) => {
      console.error("listenUsers error:", err);
      setError("Failed fetching users");
      setIsLoading(false);
    });

    listenersRef.current.push(unsub);
    return () => { try { unsub(); } catch(e){} };
  }, [currentUser]);

  //
  // Listen to chats where currentUser is participant to populate lastMessage for userChats.
  // For unreadCount we attach a per-chat messages listener (via listenToMessages) and compute unread count.
  //
  useEffect(() => {
    // cleanup prior per-user unsubscribes
    Object.values(perUserMessageUnsubs.current).forEach(fn => { try { fn(); } catch(e){} });
    perUserMessageUnsubs.current = {};

    if (!currentUser || users.length === 0) return;

    const unsubChats = listenToUserChats(currentUser.uid, (chats) => {
      // chats: array of chat docs { id, participants, lastMessage, ... }
      // build a map chatId -> chatData
      const chatMap = {};
      chats.forEach(c => { chatMap[c.id] = c; });

      // For each user in users, map to chatId and lastMessage
      users.forEach((user) => {
        const chatId = makeChatId(currentUser.uid, user.id);
        const chatData = chatMap[chatId] || null;

        // set lastMessage (might be null)
        setUserChats(prev => ({
          ...prev,
          [user.id]: {
            ...(prev[user.id] || {}),
            lastMessage: chatData?.lastMessage || null,
            // preserve unreadCount until messages listener updates it
            unreadCount: prev[user.id]?.unreadCount || 0
          }
        }));

        // If we don't already have a per-user messages listener, attach one to compute unread count
        if (!perUserMessageUnsubs.current[user.id]) {
          const unsubMsgs = listenToMessages(chatId, (msgs) => {
            const unread = msgs.filter(m => m.isRead !== true && m.senderId !== currentUser.uid).length;
            // update lastMessage safely (some messages use serverTimestamp placeholder; it's fine)
            const lastMessage = msgs.length ? msgs[msgs.length - 1] : (chatData?.lastMessage || null);
            setUserChats(prev => ({
              ...prev,
              [user.id]: {
                ...(prev[user.id] || {}),
                lastMessage,
                unreadCount: unread
              }
            }));
          }, (err) => {
            console.error("per-user messages listener error for", user.id, err);
          });

          perUserMessageUnsubs.current[user.id] = unsubMsgs;
        }
      });
    }, (err) => {
      console.error("listenToUserChats error:", err);
    });

    // store and cleanup
    listenersRef.current.push(unsubChats);
    return () => {
      try { unsubChats(); } catch(e){}
      Object.values(perUserMessageUnsubs.current).forEach(fn => { try { fn(); } catch(e){} });
      perUserMessageUnsubs.current = {};
    };
  }, [currentUser, users]);

  //
  // Messages + Typing listeners for selectedUser (active chat)
  //
  useEffect(() => {
    // cleanup prior listeners related to selectedUser
    listenersRef.current.forEach(fn => { /* keep global ones */ });
    // when selectedUser changes, remove any typing/message listeners we registered for previous chat
    // We'll manage them locally here:
    let unsubMessages = null;
    let unsubTyping = null;

    const setup = async () => {
      if (!selectedUser || !currentUser) {
        setMessages([]);
        return;
      }

      const chatId = makeChatId(currentUser.uid, selectedUser.id);

      // ensure chat doc exists (create if missing)
      try {
        await ensureChatDoc(currentUser.uid, selectedUser.id);
      } catch (err) {
        console.error("ensureChatDoc error:", err);
      }

      // messages listener
      unsubMessages = listenToMessages(chatId, (msgs) => {
        setMessages(msgs);
        // mark unread messages as read (batch) — use service
        markChatMessagesRead(chatId, currentUser.uid).catch(err => console.error("markChatMessagesRead error:", err));
      }, (err) => {
        console.error("listenToMessages error:", err);
        setError("Failed to load messages");
      });

      // typing listener
      unsubTyping = listenTyping(chatId, (typingList) => {
        const tSet = new Set(typingList.filter(id => id !== currentUser.uid));
        setTypingUsers(tSet);
      }, (err) => {
        console.error("listenTyping error:", err);
      });

      // store unsubs in listenersRef so global cleanup handles them as well
      listenersRef.current.push(unsubMessages);
      listenersRef.current.push(unsubTyping);
    };

    setup();

    return () => {
      try { unsubMessages && unsubMessages(); } catch(e){}
      try { unsubTyping && unsubTyping(); } catch(e){}
      // Also clear typingUsers set for the selectedUser when switching away
      setTypingUsers(new Set());
    };
  }, [selectedUser, currentUser]);

  //
  // markChatMessagesRead is provided by service; we keep this wrapper for compatibility with earlier code logic
  //
  const markMessagesAsRead = async (messagesList) => {
    if (!selectedUser || !currentUser) return;
    const chatId = makeChatId(currentUser.uid, selectedUser.id);
    try {
      await markChatMessagesRead(chatId, currentUser.uid);
    } catch (err) {
      console.error("markMessagesAsRead error:", err);
    }
  };

  //
  // sendMessage using service
  //
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || !currentUser) return;
    const chatId = makeChatId(currentUser.uid, selectedUser.id);

    const messageData = {
      text: newMessage,
      senderId: currentUser.uid,
      senderName: currentUser.displayName || currentUser.email || '',
      // note: service will set serverTimestamp itself
      // include participants if you want the service to set them when creating chat
      participants: [currentUser.uid, selectedUser.id]
    };

    try {
      await sendMessageToChat(chatId, messageData);
      // clear typing indicator for self
      try { await clearTyping(chatId, currentUser.uid); } catch(e){}
    } catch (err) {
      console.error("sendMessage error:", err);
      setError("Failed to send message");
    } finally {
      setNewMessage('');
    }
  };

  //
  // typing handler (setTyping via service, and clear after debounce)
  //
  const handleTyping = async () => {
    if (!selectedUser || !currentUser) {
      setIsTypingLocal(true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTypingLocal(false), 1500);
      return;
    }

    const chatId = makeChatId(currentUser.uid, selectedUser.id);

    try {
      await setTyping(chatId, currentUser.uid);
    } catch (err) {
      console.error("setTyping error:", err);
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(async () => {
      try { await clearTyping(chatId, currentUser.uid); } catch(e){ console.error("clearTyping error", e) }
    }, 3000);
  };

  // keyboard handler (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      handleTyping();
    }
  };

  // formatTime and getInitials (kept same)
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
  };

  const filteredUsers = users.filter(u =>
    (u.username || u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isUserOnline = (userId) => onlineUsers.has(userId);

  // onSelectUser ensures chat exists and selects user (keeps same UI behavior)
  const onSelectUser = async (user) => {
    setSelectedUser(user);

    if (!currentUser) return;

    const chatId = makeChatId(currentUser.uid, user.id);
    try {
      await ensureChatDoc(currentUser.uid, user.id);
    } catch (err) {
      console.error("onSelectUser ensureChatDoc error:", err);
    }
  };

  // cleanup all listeners on unmount
  useEffect(() => {
    return () => {
      // call every unsubscribe function
      listenersRef.current.forEach(fn => { try { fn(); } catch(e){} });
      listenersRef.current = [];
      Object.values(perUserMessageUnsubs.current).forEach(fn => { try { fn(); } catch(e){} });
      perUserMessageUnsubs.current = {};
      // mark offline
      if (currentUser) {
        setUserOffline(currentUser.uid, currentUser.displayName || currentUser.email || '').catch(() => {});
      }
    };
    // note: we intentionally don't add currentUser to deps because we want this to run once on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UI Loading / Error states (kept same)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiMessageCircle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 mb-2">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">Retry</button>
        </div>
      </div>
    );
  }

  // Render (kept nearly identical to your original)
  return (
    <div className="flex h-full bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Users List */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col shadow-lg flex-shrink-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-500 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Messages</h2>
            <button className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <FiMoreVertical className="w-5 h-5"/>
            </button>
          </div>
          {/* Search */}
          <div className="mt-4 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 rounded-lg border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FiUser className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No users found</p>
            </div>
          ) : (
            filteredUsers.map(user => {
              const chatInfo = userChats[user.id] || {};
              const lastMsg = chatInfo.lastMessage;
              const unread = chatInfo.unreadCount || 0;
              return (
                <div
                  key={user.id}
                  onClick={() => onSelectUser(user)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-all duration-200 ${selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg overflow-hidden">
                        {user.profileImage ? (
                          <img src={user.profileImage} alt="Profile" className="w-full h-full object-cover"/>
                        ) : (
                          getInitials(user.username || user.email)
                        )}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${isUserOnline(user.id) ? 'bg-green-400' : 'bg-gray-400'}`}></div>

                      {unread > 0 && (
                        <span className="absolute -top-1 -left-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center border-2 border-white">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800 truncate">{user.username || user.email}</h3>
                        <span className="text-xs text-gray-500">
                          {lastMsg && lastMsg.timestamp ? formatTime(lastMsg.timestamp) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {lastMsg ? (
                          <>
                            <span className="font-medium">{lastMsg.senderId === currentUser.uid ? 'You: ' : ''}</span>
                            {lastMsg.text || (lastMsg.imageUrl ? '📷 Image' : lastMsg.fileUrl ? '📎 File' : '')}
                          </>
                        ) : (user.bio || 'Dancer & Creator')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedUser ? (
          <>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between shadow-sm flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg overflow-hidden">
                    {selectedUser.profileImage ? <img src={selectedUser.profileImage} alt="Profile" className="w-full h-full object-cover" /> : getInitials(selectedUser.username || selectedUser.email)}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${isUserOnline(selectedUser.id) ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{selectedUser.username || selectedUser.email}</h3>
                  <p className="text-sm text-gray-500">
                    {isUserOnline(selectedUser.id) ? 'Online' : 'Offline'}
                    { (typingUsers.has(selectedUser.id) || (isTypingLocal && !firebaseAvailable)) && <span className="ml-2 text-blue-500">• typing</span> }
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <FiPaperclip className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <FiImage className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <BsThreeDots className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <FiMessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No messages yet</p>
                  <p className="text-sm text-gray-400">Start a conversation!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.senderId === currentUser.uid ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow-sm ${msg.senderId === currentUser.uid ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'bg-white text-gray-800 border border-gray-200'}`}>
                      <p className="text-sm">{msg.text}</p>
                      <div className={`flex items-center justify-between mt-1 ${msg.senderId === currentUser.uid ? 'text-white/70' : 'text-gray-500'}`}>
                        <span className="text-xs flex items-center"><FiClock className="w-3 h-3 mr-1" />{formatTime(msg.timestamp)}</span>
                        {msg.senderId === currentUser.uid && (
                          <span className="text-xs ml-2">
                            {msg.isRead ? <BsCheck2All className="w-3 h-3 text-blue-300" /> : <BsCheck2 className="w-3 h-3" />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    rows="1"
                    style={{ minHeight: '40px', maxHeight: '120px' }}
                  />
                </div>

                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <FiSmile className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <FiSend className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          // Welcome Screen
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FiMessageCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Chat!</h3>
              <p className="text-gray-600 mb-6">Select a user from the list to start chatting</p>
              <div className="text-sm text-gray-500 space-y-1">
                <p>• Real-time messaging</p>
                <p>• Share dance videos</p>
                <p>• Connect with dancers</p>
                <p>• Typing indicators</p>
                <p>• Read receipts</p>
                <p>• Online status</p>
                {!firebaseAvailable && (<p className="text-yellow-600 font-semibold">• Demo Mode (Firebase not configured)</p>)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSystem;
